import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useDocumentMetadata } from '../hooks/useDocumentMetadata';
import { Button } from '../components/ui/Button';
import styles from './NotFoundPage.module.css';

export const NotFoundPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [ref, visible] = useScrollReveal(0.05);

  // States: '404' (default static state), 'switching' (lever clicked, train animating), 'redirected'
  const [switchState, setSwitchState] = useState<'404' | 'switching' | 'redirected'>('404');

  useDocumentMetadata(
    t('notFound.title') + " | NexTrainE",
    t('notFound.desc')
  );

  const handleSwitch = () => {
    if (switchState !== '404') return;
    setSwitchState('switching');

    // Run the redirect after the train finishes the path animation (1.8s)
    setTimeout(() => {
      setSwitchState('redirected');
      navigate('/');
    }, 1800);
  };

  return (
    <div className={styles.page} ref={ref as React.RefObject<HTMLDivElement>}>
      {/* Light cyber grid backdrop */}
      <div className={styles.gridBackdrop} />
      <div className={styles.glowOverlay} />

      <div className={`${styles.container} ${visible ? styles.visible : ''}`}>
        {/* Glowing 404 header */}
        <div className={styles.errorCodeContainer}>
          <h1 className={styles.errorCode}>404</h1>
          <div className={styles.errorGlow}>404</div>
        </div>

        {/* Interactive Track Switcher Widget */}
        <div className={styles.switchWidget}>
          <div className={styles.widgetHeader}>
            <span className={styles.widgetIndicator}>
              {switchState === '404' ? '▲ ROUTE_FAULT' : '▼ REROUTING_ACTIVE'}
            </span>
            <span className={styles.widgetTitle}>TELEMETRY_TRACK_SWITCH</span>
          </div>

          <div className={styles.widgetContent}>
            {/* SVG Track Display */}
            <div className={styles.svgWrapper}>
              <svg viewBox="0 0 280 100" className={styles.trackSvg}>
                {/* Rails Backdrop */}
                {/* Base/Common Track */}
                <path d="M 10 50 L 90 50" className={styles.trackBase} />
                
                {/* 404 Branch (Upper) */}
                <path 
                  d="M 90 50 C 130 50, 160 20, 230 20" 
                  className={`${styles.trackBranch} ${switchState === '404' ? styles.trackBranchActiveError : styles.trackBranchDimmed}`} 
                />
                
                {/* Home Branch (Lower) */}
                <path 
                  d="M 90 50 C 130 50, 160 80, 230 80" 
                  className={`${styles.trackBranch} ${switchState === 'switching' ? styles.trackBranchActiveSuccess : styles.trackBranchDimmed}`} 
                />

                {/* Switch Blade (Pivoting segment at x=90, y=50) */}
                <line 
                  x1="90" y1="50" 
                  x2="120" y2={switchState === '404' ? "41" : "59"} 
                  className={styles.switchBlade} 
                />

                {/* Nodes/Endpoints */}
                {/* Start Node */}
                <circle cx="10" cy="50" r="4" className={styles.endpointNode} />

                {/* 404 Node */}
                <g transform="translate(230, 20)">
                  <circle 
                    cx="0" cy="0" r="6" 
                    className={`${styles.targetNode} ${switchState === '404' ? styles.targetNodeError : ''}`} 
                  />
                  <text x="12" y="4" className={styles.nodeText}>404</text>
                </g>

                {/* Home Node */}
                <g transform="translate(230, 80)">
                  <circle 
                    cx="0" cy="0" r="6" 
                    className={`${styles.targetNode} ${switchState === 'switching' ? styles.targetNodeSuccess : ''}`} 
                  />
                  <text x="12" y="4" className={styles.nodeText}>HOME</text>
                </g>

                {/* Animated Train Node (Visible only when switching) */}
                {switchState === 'switching' && (
                  <g className={styles.trainNode}>
                    <circle cx="0" cy="0" r="5" className={styles.trainPulse} />
                    <circle cx="0" cy="0" r="3" className={styles.trainCore} />
                  </g>
                )}
              </svg>
            </div>

            {/* Lever Switch Interface */}
            <div className={styles.leverInterface}>
              <button 
                onClick={handleSwitch}
                disabled={switchState !== '404'}
                className={`${styles.leverButton} ${switchState !== '404' ? styles.leverButtonDisabled : ''}`}
                aria-label="Toggle track switch"
              >
                <div className={styles.leverTrack}>
                  <div className={`${styles.leverKnob} ${switchState !== '404' ? styles.leverKnobActive : ''}`} />
                </div>
                <span className={styles.leverText}>
                  {switchState === '404' ? t('notFound.lever_ready') : t('notFound.lever_active')}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Copy text */}
        <div className={styles.textContainer}>
          <h2 className={styles.title}>{t('notFound.title')}</h2>
          <p className={styles.desc}>{t('notFound.desc')}</p>
        </div>

        {/* Diagnostic log details */}
        <div className={styles.diagnosticsBox}>
          <div className={styles.terminalHeader}>
            <span className={styles.terminalDot} />
            <span className={styles.terminalDot} />
            <span className={styles.terminalDot} />
            <span className={styles.terminalTitle}>TRAIN_ROUTING_DIAGNOSTICS.log</span>
          </div>
          <div className={styles.terminalBody}>
            <div className={styles.logLine}>
              <span className={styles.lbl}>[LINE_STATUS]</span> <span className={styles.valErr}>OUT_OF_SERVICE</span>
            </div>
            <div className={styles.logLine}>
              <span className={styles.lbl}>[TRACK_ID]</span> <span className={styles.valPath}>{location.pathname}</span>
            </div>
            <div className={styles.logLine}>
              <span className={styles.lbl}>[SIGNAL_CODE]</span> <span className={styles.val}>0x404_UNSCHEDULED_DETOUR</span>
            </div>
            <div className={styles.logLine}>
              <span className={styles.lbl}>[NEXT_SWITCH]</span> <span className={styles.valWarn}>ROUTE_TO_STATION_HOME</span>
            </div>
          </div>
        </div>

        {/* Standard CTA Action Link */}
        <div className={styles.actionContainer}>
          <Button variant="secondary" size="small" href="/">
            {t('notFound.back_home')}
          </Button>
        </div>
      </div>
    </div>
  );
};
